import React from 'react'
import MultipleChoiceCard from '../components/QuestionsPreparation/MultpleChoiceCard'
import DescriptiveTextCard from '../components/QuestionsPreparation/DescriptiveTextCard'
import FreeResponseCard from '../components/QuestionsPreparation/FreeResponseCard'
import { type Question, SurveyItemQuestionType, type MultiChoiceQuestion, type FreeResponseQuestion, type DescriptiveQuestion, type LinearQuestion } from '../types'
import CreateMultipleChoiceQuestionCard from '../components/QuestionsCreation/CreateMultipleChoiceQuestionCard'
import CreateDescriptiveQuestionCard from '../components/QuestionsCreation/CreateDescriptiveQuestionCard'
import CreateFreeResponseQuestionCard from '../components/QuestionsCreation/CreateFreeResponseQuestionCard'
import { StyledQuestionsWrapper } from './styles'
import CreateLinearScaleCard from '../components/QuestionsCreation/CreateLinearScaleCard'
import LinearQuestionCard from '../components/QuestionsPreparation/LinearQuestionCard'
import { getEditableQuestion } from '../../../store/slices/SurveyTemplateSlice/selectors'
import CreateMultiSelectQuestionCard from '../components/QuestionsCreation/CreateMultiSelectQuestionCard'
import MultiSelectCard from '../components/QuestionsPreparation/MultiSelectCard'
import { type ISurveySection } from '../../../store/slices/SurveyTemplateSlice'
import { useLocation } from 'react-router-dom'
import ROUTES from '../../../consts/paths'

interface SurveyPreparedQuestionsProps {
  surveyItems: any
  sectionIndex: number
  section: ISurveySection
  isAdminAddedQuestion?: boolean
  isCountComplete?: boolean
  arrayOfSchoolApprovedQuestionIds?: string[]
  isSurveyTemplate?: boolean
}

const SurveyPreparedQuestions: React.FC<SurveyPreparedQuestionsProps> = ({
  surveyItems,
  sectionIndex,
  section,
  isAdminAddedQuestion,
  isCountComplete,
  arrayOfSchoolApprovedQuestionIds,
  isSurveyTemplate = false
}): JSX.Element => {
  const questions = surveyItems
  const editableQuestion = getEditableQuestion()
  const totalQuestionInSection = surveyItems?.length
  let questionNumber = 0

  const location = useLocation()
  const isAcademicRoute = location.pathname.includes(ROUTES.ACADEMIC_UNIT_ADDED_QUESTIONS)

  const showPreparedQuestions = (question: Question, index: number, isEditable: boolean): JSX.Element => {
    const commonProps = { questionNumber, index, sectionIndex, totalQuestionInSection, section, isEditable, isSurveyTemplate }
    switch (question?.itemType) {
      case SurveyItemQuestionType.MULTIPLE_CHOICE:
        return <MultipleChoiceCard question={question as MultiChoiceQuestion} {...commonProps}/>
      case SurveyItemQuestionType.FREE_RESPONSE:
        return <FreeResponseCard question={question as FreeResponseQuestion} {...commonProps} />
      case SurveyItemQuestionType.DESCRIPTIVE_TEXT:
        return <DescriptiveTextCard question={question as DescriptiveQuestion} index={index} sectionIndex={sectionIndex} totalQuestionInSection={totalQuestionInSection} section={section}/>
      case SurveyItemQuestionType.LINEAR_SCALE:
        return <LinearQuestionCard question={question as LinearQuestion} {...commonProps} />
      case SurveyItemQuestionType.MULTI_SELECT:
        return <MultiSelectCard question={question as MultiChoiceQuestion} {...commonProps} />
      default:
        return <></>
    }
  }

  const showEditableQuestion = (index: number): JSX.Element => {
    const props = {
      sectionIndex,
      questionIndex: index,
      questionNumber,
      section,
      isAdminAddedQuestion,
      isCountComplete
    }

    switch (editableQuestion?.question.itemType) {
      case SurveyItemQuestionType.MULTIPLE_CHOICE:
        return <CreateMultipleChoiceQuestionCard {...props}/>
      case SurveyItemQuestionType.DESCRIPTIVE_TEXT:
        return <CreateDescriptiveQuestionCard {...props}/>
      case SurveyItemQuestionType.FREE_RESPONSE:
        return <CreateFreeResponseQuestionCard {...props} />
      case SurveyItemQuestionType.LINEAR_SCALE:
        return <CreateLinearScaleCard {...props}/>
      case SurveyItemQuestionType.MULTI_SELECT:
        return <CreateMultiSelectQuestionCard {...props}/>
      default:
        return <></>
    }
  }

  const renderQuestion = (question: Question, index: number): JSX.Element => {
    const isEditable = !arrayOfSchoolApprovedQuestionIds?.includes(question.itemId ?? '') || isAcademicRoute
    if (editableQuestion != null &&
        sectionIndex === editableQuestion.sectionIndex &&
        index === editableQuestion?.questionIndex &&
        isEditable
    ) {
      return showEditableQuestion(index)
    }
    return showPreparedQuestions(question, index, isEditable)
  }

  const getQuestionNumber = (question: Question): void => {
    if (question?.itemType !== SurveyItemQuestionType.DESCRIPTIVE_TEXT) {
      questionNumber++
    }
  }

  return (
    <StyledQuestionsWrapper>
      {
        questions?.map((question: Question, index: number) => {
          getQuestionNumber(question)
          return renderQuestion(question, index)
        })
      }
    </StyledQuestionsWrapper>
  )
}

export default SurveyPreparedQuestions
